import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Stack,
    TextField,
    MenuItem,
    Box,
    Typography,
    IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import styled from '@emotion/styled';
// import { PaymentRecord } from '../types/counseling';


interface PaymentRecord {
    입출금번호: number;
    일시: string;
    구분: '입금' | '출금' | '환불' | '환입';
    입출대상: string;
    내역: string;
    금액: number;
}

const StyledTextField = styled(TextField)`
    width: 100%;
    
    & .MuiOutlinedInput-root {
        height: 32px;
        font-size: 14px;
        background-color: #fff;
        
        &:hover fieldset {
            border-color: #1976d2;
        }
        
        &.Mui-focused fieldset {
            border-color: #1976d2;
        }

        &.Mui-disabled {
            background-color: #f5f5f5;
        }

        & input {
            text-align: left;
        }
    }

    & .MuiOutlinedInput-input {
        padding: 4px 8px;
        text-align: left;
    }

    &.right-align .MuiOutlinedInput-input {
        text-align: right;
    }
` as typeof TextField;

interface PaymentManageDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (입금총액: number, 출금총액: number, records: PaymentRecord[]) => void;
    initialRecords?: PaymentRecord[];
}

const PaymentManageDialog: React.FC<PaymentManageDialogProps> = ({
    open,
    onClose,
    onSave,
    initialRecords = []
}) => {
    const [records, setRecords] = useState<PaymentRecord[]>(initialRecords);

    useEffect(() => {
        setRecords(initialRecords);
    }, [initialRecords]);

    const handleAddRecord = () => {
        const newRecord = {
            입출금번호: Date.now(),
            일시: new Date().toISOString().split('T')[0],
            구분: '입금' as const,
            입출대상: '',
            내역: '',
            금액: 0,
        };
        setRecords([...records, newRecord]);
    };

    const handleDeleteRecord = (입출금번호: number) => {
        setRecords(records.filter(record => record.입출금번호 !== 입출금번호));
    };
    const handleRecordChange = (입출금번호: number, field: keyof PaymentRecord, value: any) => {
    setRecords(records.map(record => {
        if (record.입출금번호 === 입출금번호) {
            return { ...record, [field]: value };   
        }
        return record;
    }));
};


    const handleSave = () => {
        const 입금총액 = records.reduce((sum, record) => {
            if (record.구분 === '입금') return sum + record.금액;
            if (record.구분 === '환불') return sum - record.금액;
            return sum;
        }, 0);

        const 출금총액 = records.reduce((sum, record) => {
            if (record.구분 === '출금') return sum + record.금액;
            if (record.구분 === '환입') return sum - record.금액;
            return sum;
        }, 0);

        onSave(입금총액, 출금총액, records);
        onClose();
    };

    const 입금총액 = records.reduce((sum, record) => {
        if (record.구분 === '입금') return sum + record.금액;
        if (record.구분 === '환불') return sum - record.금액;
        return sum;
    }, 0);

    const 출금총액 = records.reduce((sum, record) => {
        if (record.구분 === '출금') return sum + record.금액;
        if (record.구분 === '환입') return sum - record.금액;
        return sum;
    }, 0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <Box sx={{ width: '100%', p: 3 , minHeight: '550px', maxHeight: '900px'}}>
                <DialogTitle sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    p: 3,
                    margin: -3,
                    marginBottom: 3,
                    height: '64px'
                    
                }}>
                    <Typography variant="h6" sx={{ flex: 1, fontSize: '1rem' }}>입/출금 관리</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            onClick={handleAddRecord}
                            sx={{ height: '32px', minWidth: '80px', bgcolor: 'success.main' }}
                            startIcon={<AddIcon />}
                        >
                            추가
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            sx={{ height: '32px', minWidth: '80px', bgcolor: 'info.main' }}
                        >
                            적용
                        </Button>
                        <Button
                            variant="contained"
                            onClick={onClose}
                            sx={{ height: '32px', minWidth: '80px', bgcolor: 'grey.600' }}
                        >
                            닫기
                        </Button>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ p: 0, mt: 2 , display: 'flex', flexDirection: 'column'}}>
                    <TableContainer component={Paper}>
                        <Table size="small" sx={{
                            '& .MuiTableCell-root': {
                                padding: '6px 6px'
                            }
                        }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>일시</TableCell>
                                    <TableCell>구분</TableCell>
                                    <TableCell>입출대상</TableCell>
                                    <TableCell>내역</TableCell>
                                    <TableCell align="right">금액</TableCell>
                                    <TableCell align="center">삭제</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {records.map((record) => (
                                    <TableRow key={record.입출금번호}>
                                        <TableCell>
                                            <StyledTextField
                                                type="date"
                                                size="small"
                                                value={record.일시}
                                                onChange={(e) => handleRecordChange(record.입출금번호, '일시', e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <StyledTextField
                                                select
                                                value={record.구분}
                                                onChange={(e) => handleRecordChange(record.입출금번호, '구분', e.target.value)}
                                            >
                                                <MenuItem value="입금">입금</MenuItem>
                                                <MenuItem value="출금">출금</MenuItem>
                                                <MenuItem value="환불">환불</MenuItem>
                                                <MenuItem value="환입">환입</MenuItem>
                                            </StyledTextField>
                                        </TableCell>
                                        <TableCell>
                                            <StyledTextField
                                                size="small"
                                                value={record.입출대상}
                                                onChange={(e) => handleRecordChange(record.입출금번호, '입출대상', e.target.value)}
                                                placeholder="입출대상"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <StyledTextField
                                                size="small"
                                                value={record.내역}
                                                onChange={(e) => handleRecordChange(record.입출금번호, '내역', e.target.value)}
                                                placeholder="내역"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <StyledTextField
                                                size="small"
                                                type="text"
                                                value={record.금액.toLocaleString('ko-KR')}
                                                onChange={(e) => {
                                                    const value = Number(e.target.value.replace(/,/g, ''));
                                                    handleRecordChange(record.입출금번호, '금액', value);
                                                }}
                                                className="right-align"
                                                inputProps={{ 
                                                    style: { 
                                                        textAlign: 'right',
                                                        color: ['환불', '환입'].includes(record.구분) ? '#ff0000' : 'inherit'
                                                    } 
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => handleDeleteRecord(record.입출금번호)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ 
                        display: 'flex', 
                        
                        justifyContent: 'space-between',
                        marginTop: 'auto',
                        mt: 2, 
                        pl: 2,                   // 왼쪽 패딩 추가
                        mb: 1
                    }}>
                        <Box sx={{ display: 'flex', mt: 1, gap: 3 }}>
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                                * 총입금액 : 구분값 (입금)-(환불)
                            </Typography>
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                                * 총출금액 : 구분값 (출금)-(환입)
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex',  mt: 1 , gap: 5 , pr: 3}}>
                            <Typography sx={{ fontSize: '14px' }}>
                                총입금액: {입금총액.toLocaleString('ko-KR')}원
                            </Typography>
                            <Typography sx={{ fontSize: '14px' }}>
                                총출금액: {출금총액.toLocaleString('ko-KR')}원
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
            </Box>
        </Dialog>
    );
};

export default PaymentManageDialog;
